import { HttpEvent, HttpEventType } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { exhaustMap, filter, map, Subject, Subscription } from 'rxjs';
import { SdVoteId, sdVoteOption } from '../../../../../shared/constants/sd-ballot.constants';
import { IApiResponse } from '../../../../../shared/models/api-response';
import { IAppFeaturesSettings } from '../../../../../shared/models/app-features-settings';
import { IVoteOption } from '../../../../../shared/models/ballot-vote';
import { ConfigurationService } from '../../../../../shared/services/configuration.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { IFileUpload } from '../../../../controls/files-upload/files-upload.component';
import { ISelectedVoteOption } from '../../../shared/models/ballot-base/proxy-vote/selected-vote-option';
import { ISdVoteDto } from '../../../shared/models/sd/sd-vote';
import { ISubmitSdBallotProxyVoteRequest } from '../../../shared/models/sd/submit-sd-ballot-proxy-vote-request ';
import { SdBallotService } from '../../../shared/services/sd-ballot.service';


@Component({
    selector: 'app-sd-proxy-vote',
    templateUrl: './sd-proxy-vote.component.html',
    styleUrl: './sd-proxy-vote.component.scss',
    standalone: false
})
export class SdProxyVoteComponent implements OnInit, OnDestroy {
  @Input() voteData!: ISdVoteDto;
  sdVoteOptions: { [key: string]: IVoteOption } = {};
  subscriptions: Subscription = new Subscription();
  isCoaManagedBySf?: boolean;
  initialSelectedVoteOptionId: SdVoteId | null = null;
  submit$: Subject<boolean> = new Subject<boolean>();

  authDocUploadProgress: number[] = [0];

  sdProxyVoteValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {

    const selectedVoteOptionCtrl: FormControl = control.get("proxyVoteOptionsCtrl") as FormControl;
    let selectedVoteOption: ISelectedVoteOption<SdVoteId> = selectedVoteOptionCtrl?.value;
    let hasVoteOption = selectedVoteOption?.isChecked;

    const selectedCertificateOfAppreciationCtrl: FormControl = control.get("certificateOfAppreciationCtrl") as FormControl;
    let isValidCoa: boolean | null | undefined = this.isCoaManagedBySf || (selectedCertificateOfAppreciationCtrl?.value !== undefined && selectedCertificateOfAppreciationCtrl?.value !== null);

    const authDocUpload: IFileUpload = (control.get('authorisationDocumentCtrl') as FormControl).value;
    const hasAuthDoc = !authDocUpload?.hasError && authDocUpload?.files.length > 0 && authDocUpload?.files[0];

    return hasVoteOption && isValidCoa && hasAuthDoc ? null : { invalidForm: true };
  };


  sdProxyVoteForm: FormGroup = new FormGroup({
    proxyVoteOptionsCtrl: new FormControl (),
    certificateOfAppreciationCtrl: new FormControl(),
    authorisationDocumentCtrl: new FormControl(),
  },
    { validators: this.sdProxyVoteValidator }
  );

  constructor(private activeModal: NgbActiveModal,
              private _sdBallotService: SdBallotService,
              private _toastService: ToastService,
              private _configService: ConfigurationService)
              {}

  ngOnInit(): void {
    this.sdVoteOptions = {};
    this.sdVoteOptions[SdVoteId.VT_AGREE] = sdVoteOption[SdVoteId.VT_AGREE];
    this.sdVoteOptions[SdVoteId.VT_DISAGREE] = sdVoteOption[SdVoteId.VT_DISAGREE];

    this.loadAppFeaturesSettings();
    this.setupSubmitFormSubscription();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  loadAppFeaturesSettings() {
    this.subscriptions.add(this._configService.getAppFeaturesSettings()
    .subscribe({
      next: (appFeaturesSettingsResponse: IApiResponse<IAppFeaturesSettings>) => {
        if (appFeaturesSettingsResponse?.success && appFeaturesSettingsResponse.content) {
          this.isCoaManagedBySf = appFeaturesSettingsResponse.content.coaManagedInSf;
        }
       },
       error: (error) => {
         console.error('SdProxyVote: Error loading app features settings.', error);
       }
    }));
  }

  setupSubmitFormSubscription(): void {
    const errorToast: string = "Your vote was not placed due to an unexpected error. Please try again or contact IT Support if issue persists.";
    const errorToastTitle: string = "Vote Unsuccessful";

    this.subscriptions.add(this.submit$.pipe(
      map(() : ISubmitSdBallotProxyVoteRequest | null => {
        if (this.sdProxyVoteForm.valid && this.voteData) {
          let formData: IProxyVoteFormData = this.sdProxyVoteForm.value;
          let request = this.mapFormToRequest(formData);
          return request;
        }
        return null;
      }),
      filter((request) => request !== null),
      exhaustMap((request) => this._sdBallotService.submitSdProxyVote(request as ISubmitSdBallotProxyVoteRequest))
    )
    .subscribe({
      next: (event: HttpEvent<any>) => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            if (event.total) {
              this.authDocUploadProgress[0] = Math.round(
                (100 * event.loaded) / event.total
              );
            }
            break;
          case HttpEventType.Response:
            this.authDocUploadProgress[0] = 100;
            const apiResponse : IApiResponse<any> = event.body;
            if (apiResponse && apiResponse.success) {
              this._toastService.showSuccess("Your vote has been submitted. Thank you!", "Vote Successful");
            } else {
              console.log('Error submitting Sd Proxy Vote.', apiResponse.message);
              this._toastService.showError(errorToast, errorToastTitle);
            }
            this.close(this.voteData.ballotRecordId.toString());
            break;
        }
      },
      error: (error) => {
        this.authDocUploadProgress[0] = -1;
        console.log('Error submitting Sd Proxy Vote.', error);
        this._toastService.showError(errorToast, errorToastTitle);
        this.close(this.voteData.ballotRecordId.toString());
      }
    }));
  }

  mapFormToRequest(formData: IProxyVoteFormData): ISubmitSdBallotProxyVoteRequest {
    return {
      ballotId: this.voteData.ballotRecordId,
      ballotVoteId: this.voteData.ballotVoteId,
      voteTypeId: formData.proxyVoteOptionsCtrl ? formData.proxyVoteOptionsCtrl.voteOptionId : null,
      comment: formData.proxyVoteOptionsCtrl ? formData.proxyVoteOptionsCtrl.comment : null,
      isCOA: !!formData.certificateOfAppreciationCtrl,
      file: formData.authorisationDocumentCtrl?.files[0],
    };
  }

  submit() : void {
    if (this.sdProxyVoteForm.valid) {
      this.submit$.next(true);
    }
  }

  close(ballotId: string = '') {
    if (!ballotId || ballotId === '') {
      this.activeModal.close();
    } else {
      this.activeModal.close(ballotId);
    }
  }
}

interface IProxyVoteFormData {
  proxyVoteOptionsCtrl: ISelectedVoteOption<SdVoteId>;
  certificateOfAppreciationCtrl: boolean;
  authorisationDocumentCtrl: IFileUpload;
}
